import { useEffect, useState } from 'react'
import { NavLink, Navigate, Outlet } from 'react-router-dom'
import { AlertTriangle, LogOut, BadgeCheck } from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { useMe, useMeState } from '../../lib/me.js'
import { canIssueWarning, canViewSalary } from '../../lib/role.js'
import EmployeeWarning from '../../pages/employee/EmployeeWarning.jsx'

/**
 * พนักงาน › เอกสาร — the two personnel documents that are not the employee record
 * itself: หนังสือเตือน and การลาออก.
 *
 * ONE top-level tab holding two, rather than two more tabs on a row that already
 * has six. Both are low-volume (monthly, not daily) and both are "a document about
 * a person" rather than roster or payroll work, so they group honestly.
 *
 * ⚠️ THE TWO TABS HAVE DIFFERENT GATES, and that is why หนังสือเตือน carries its own
 * check while ลาออก does not:
 *   · การลาออก  — Manager/GM/HR/Admin. A branch manager NEEDS it: they backfill the
 *     shift. (The off-boarding ANSWERS inside are HR/Admin only, enforced in Xano
 *     and by the /api/hr/resignation-survey allowlist — not by hiding a tab.)
 *   · หนังสือเตือน — HR/GM/Admin only, per the owner. A plain branch Manager holding
 *     no GM role must not see the register at all, and Xano's GET
 *     /employee_warning already scopes them to their own — so without this gate the
 *     tab would load and show them an empty page, which reads as "there are none".
 *     A tab that lies is worse than a tab that is absent.
 */
export default function DocsLayout() {
  const me = useMe()
  const mayWarn = canIssueWarning(me)
  // ⚠️ canViewSalary, NOT canIssueWarning — every row of the register carries the
  // salary figure printed on a certificate, so it follows PAYROLL's gate (HR/Admin)
  // and not this tab row's other one. canIssueWarning admits GM, which is four
  // branch managers as well as the two owners. _middleware.js refuses
  // /api/hr/employee-document for them and Xano checks it again — this only hides
  // a tab that would otherwise load an empty page and read as "there are none".
  const mayAudit = canViewSalary(me)
  const [pending, setPending] = useState(0)

  useEffect(() => {
    let alive = true
    apiGet('/api/hr/resignation', { status: 'pending' })
      .then((r) => { if (alive) setPending(Number(r?.pending_count || 0)) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const tabs = [
    ...(mayWarn ? [{ to: '', end: true, label: 'หนังสือเตือน', icon: AlertTriangle, hint: 'ออกและติดตามหนังสือเตือน' }] : []),
    { to: 'resignation', label: 'ลาออก', icon: LogOut, hint: 'หนังสือแจ้งลาออกและแบบสอบถามก่อนพ้นสภาพ', badge: pending },
    // No badge, and there must never be one: staff issue these without approval, so
    // nothing here is ever pending. A count would promise a queue that does not exist.
    ...(mayAudit ? [{ to: 'certificate', label: 'ใบรับรอง', icon: BadgeCheck, hint: 'ทะเบียนหนังสือรับรองเงินเดือน / รับรองการทำงาน ที่พนักงานออกเอง' }] : []),
  ]

  return (
    <div>
      <nav className="flex items-center gap-1 mb-5">
        {tabs.map(({ to, end, label, icon: Icon, hint, badge }) => (
          <NavLink
            key={to || 'index'}
            to={to ? `/manager/employee/docs/${to}` : '/manager/employee/docs'}
            end={end}
            title={hint}
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${
                isActive
                  ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100 font-medium'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
              }`
            }
          >
            <Icon size={13} strokeWidth={1.75} />
            <span>{label}</span>
            {badge > 0 && (
              <span className="ml-0.5 rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200 px-1.5 text-[11px] font-semibold leading-[17px]">
                {badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}

/**
 * The /manager/employee/docs INDEX. Hiding the หนังสือเตือน tab above is not enough
 * on its own: the เอกสาร tab links to /docs, and its index used to BE the warning
 * register — so a plain Manager landed on the very page the gate hides. Here the
 * index renders the register only for those allowed it, and sends everyone else to
 * the first tab they can see (ลาออก, which every docs user holds).
 */
export function DocsIndex() {
  const { me, status } = useMeState()
  // Wait for the user: deciding on a null `me` would bounce a GM off the register.
  if (status === 'loading' && !me) return null
  return canIssueWarning(me)
    ? <EmployeeWarning />
    : <Navigate to="/manager/employee/docs/resignation" replace />
}
